import React, { useState, useEffect } from 'react';
import Header from '../Header';
import axios from 'axios';
import Cookies from 'js-cookie';
import LogOut from '../Account/LogOut';
import {
  CircularProgress,
  Box,
  Container,
  Paper,
  Typography,
  Alert,
  AlertTitle,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Grid,
  Card,
  CardContent,
  CardActions,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Tooltip,
  Badge,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Rating,
} from '@mui/material';
import {
  Chat,
  ArrowBack,
  Search,
  FilterList,
  Sort,
  ExpandMore,
  Visibility,
  Timeline,
  Assignment,
  BugReport,
  Support,
  CheckCircle,
  Schedule,
  Error as ErrorIcon,
  Lock,
  Add,
  WavingHand,
  EmojiEmotions,
  Celebration,
  ThumbUp,
  Help,
  Feedback,
} from '@mui/icons-material';
import TicketChat from '../Chat/TicketChat';
import UIEnhancementTicket from './UIEnhancementTicket';
import EnhancedNewTicketDialog from './EnhancedNewTicket';
import EnhancedHeader from '../EnhancedHeader';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import './TicketTable.css';
import './EnhancedTicketTable.css';
import '../EnhancedHeader.css';

const MySwal = withReactContent(Swal);

const EnhancedTicketTable = () => {
  // State management
  const [cases, setCases] = useState([]);
  const [filteredCases, setFilteredCases] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [showChat, setShowChat] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [showUIEnhancementDialog, setShowUIEnhancementDialog] = useState(false);
  const [showNewTicketDialog, setShowNewTicketDialog] = useState(false);
  const [feedbackDialog, setFeedbackDialog] = useState({ open: false, ticket: null });
  const [feedbackRating, setFeedbackRating] = useState(0);
  const [feedbackComment, setFeedbackComment] = useState('');
  const [submittingFeedback, setSubmittingFeedback] = useState(false);
  
  // Filter and search states
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [categoryFilter, setCategoryFilter] = useState('All');
  const [sortBy, setSortBy] = useState('CreatedOn');
  const [sortOrder, setSortOrder] = useState('desc');
  const [dateRange, setDateRange] = useState('All');

  // Get status icon based on status
  const getStatusIcon = (status) => {
    switch (status) {
      case 'Resolved':
      case 'Site Visit + Resolved':
        return <CheckCircle color="success" />;
      case 'Unassigned':
        return <Assignment color="info" />;
      case 'In Progress':
        return <Schedule color="warning" />;
      default:
        return <ErrorIcon color="error" />;
    }
  };

  // Get priority based on status and age
  const getTicketPriority = (ticket) => {
    const createdDate = new Date(ticket.CreatedOn);
    const daysSinceCreated = Math.floor((new Date() - createdDate) / (1000 * 60 * 60 * 24));
    
    if (ticket.Status === 'Unassigned' && daysSinceCreated > 3) return 'High';
    if (daysSinceCreated > 7) return 'Medium';
    return 'Low';
  };

  const formatDate = (dateString) => {
    const options = { 
      weekday: 'short', 
      day: 'numeric', 
      month: 'short', 
      year: 'numeric', 
      hour: 'numeric', 
      minute: 'numeric' 
    };
    return new Date(dateString).toLocaleString('en-US', options);
  };

  // Enhanced data retrieval with processing
  const RetrieveData = async () => {
    setIsLoading(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_Back_URL}/tickets/casesfilter`,
        { id: Cookies.get('token') },
        { 
          timeout: 15000, // 15 second timeout
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );
      
      const updatedCases = response.data.map(dataItem => ({
        ID: dataItem.new_caseid,
        Name: dataItem.new_name,
        Category: `${dataItem.new_cat1srng} | ${dataItem.new_cat2srng} | ${dataItem.new_cat3srng}`,
        Status: dataItem.new_statussrng,
        Description: dataItem.new_description,
        Owner: dataItem.new_ownersrnng,
        Feedback: dataItem.new_feedback,
        Comments: dataItem.new_supportcomments || "No comments",
        CreatedOn: dataItem.createdon,
        FormattedDate: formatDate(dataItem.createdon),
        color: dataItem.new_statussrng === "Unassigned" ? "info" :
               (dataItem.new_statussrng === "Resolved" || dataItem.new_statussrng === "Site Visit + Resolved" || dataItem.new_statussrng.toLowerCase() === "closed") ? "success" : "warning",
        priority: null // Will be calculated later
      }));

      // Add priority calculation
      const casesWithPriority = updatedCases.map(ticket => ({
        ...ticket,
        priority: getTicketPriority(ticket)
      }));

      setCases(casesWithPriority);
      setFilteredCases(casesWithPriority);
      setIsLoading(false);
    } catch (error) {
      console.error('Error retrieving data:', error);
      setIsLoading(false);
      
      if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
        MySwal.fire({
          icon: 'error',
          title: 'Request Timeout',
          text: 'The server took too long to respond. Please check your connection and try again.',
          confirmButtonColor: '#B41736',
        });
      } else {
        LogOut();
      }
    }
  };

  // Filter and search functionality
  useEffect(() => {
    let filtered = [...cases];

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(ticket =>
        ticket.Name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        ticket.Description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        ticket.Category.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Status filter
    if (statusFilter !== 'All') {
      filtered = filtered.filter(ticket => ticket.Status === statusFilter);
    }

    // Category filter
    if (categoryFilter !== 'All') {
      filtered = filtered.filter(ticket => ticket.Category.includes(categoryFilter));
    }

    // Date range filter
    if (dateRange !== 'All') {
      const now = new Date();
      const filterDate = new Date();
      
      switch (dateRange) {
        case 'Today':
          filterDate.setDate(now.getDate() - 1);
          break;
        case 'Week':
          filterDate.setDate(now.getDate() - 7);
          break;
        case 'Month':
          filterDate.setMonth(now.getMonth() - 1);
          break;
        default:
          break;
      }
      
      if (dateRange !== 'All') {
        filtered = filtered.filter(ticket => new Date(ticket.CreatedOn) >= filterDate);
      }
    }

    // Sorting
    filtered.sort((a, b) => {
      let aVal = a[sortBy];
      let bVal = b[sortBy];

      if (sortBy === 'CreatedOn') {
        aVal = new Date(aVal);
        bVal = new Date(bVal);
      }

      if (sortOrder === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });

    setFilteredCases(filtered);
  }, [cases, searchTerm, statusFilter, categoryFilter, sortBy, sortOrder, dateRange]);

  useEffect(() => {
    RetrieveData();
  }, []);

  // Get unique statuses and categories for filters
  const uniqueStatuses = [...new Set(cases.map(ticket => ticket.Status))];
  const uniqueCategories = [...new Set(cases.map(ticket => ticket.Category.split(' | ')[0]))];

  const handleChatClick = (ticket) => {
    setSelectedTicket(ticket);
    setShowChat(true);
  };

  const handleDetailsClick = (ticket) => {
    setSelectedTicket(ticket);
    setShowDetails(true);
  };

  const handleBackToTickets = () => {
    setShowChat(false);
    setShowDetails(false);
    setSelectedTicket(null);
  };

  const handleFeedbackClick = (ticket) => {
    setFeedbackDialog({ open: true, ticket });
    setFeedbackRating(0);
    setFeedbackComment('');
  };

  const handleCloseFeedbackDialog = () => {
    setFeedbackDialog({ open: false, ticket: null });
    setFeedbackRating(0);
    setFeedbackComment('');
  };

  const handleSubmitFeedback = async () => {
    if (feedbackRating === 0) {
      alert('Please select a rating before submitting.');
      return;
    }

    setSubmittingFeedback(true);
    
    try {
      const formattedFeedback = `Rating : ${feedbackRating}\nComments: ${feedbackComment.trim()}`;
      
      const requestBody = {
        id: feedbackDialog.ticket.ID,
        new_feedback: formattedFeedback
      };

      await axios.post(
        `${process.env.REACT_APP_Back_URL}/tickets/updatecase`, 
        requestBody,
        { timeout: 15000 }
      );
      
      // Update the local cases state
      setCases(prevCases => 
        prevCases.map(ticket => 
          ticket.ID === feedbackDialog.ticket.ID 
            ? { ...ticket, Feedback: formattedFeedback }
            : ticket
        )
      );
      
      handleCloseFeedbackDialog();
      alert('Feedback submitted successfully!');
    } catch (error) {
      console.error('Error submitting feedback:', error);
      alert('Failed to submit feedback. Please try again.');
    } finally {
      setSubmittingFeedback(false);
    }
  };

  const handleCommentChange = (event) => {
    const value = event.target.value;
    if (value.length <= 1900) {
      setFeedbackComment(value);
    }
  };

  const handleUIEnhancementSubmit = async (ticketData) => {
    try {
      await axios.post(
        `${process.env.REACT_APP_Back_URL}/tickets/cases`, 
        ticketData,
        { timeout: 15000 }
      );
      // Refresh the tickets list
      await RetrieveData();
    } catch (error) {
      console.error('Error creating UI enhancement ticket:', error);
      throw error;
    }
  };

  // Statistics
  const getStatistics = () => {
    const total = cases.length;
    const resolved = cases.filter(t => t.Status.includes('Resolved')).length;
    const closed = cases.filter(t => t.Status.toLowerCase() === 'closed').length;
    const inProgress = cases.filter(t => !t.Status.includes('Resolved') && t.Status.toLowerCase() !== 'closed' && t.Status !== 'Unassigned').length;

    return { total, resolved, closed, inProgress };
  };

  const stats = getStatistics();

  return (
    <>
      <EnhancedHeader onTicketCreated={RetrieveData} />
      
      {/* Animated Background Wrapper */}
      <Box
        sx={{
          minHeight: "100vh",
          background: "#51647C",
          position: "relative",
          overflow: "hidden",
          pb: 4,
        }}
      >
        {/* Animated Background Elements */}
        <Box
          sx={{
            position: "absolute",
            top: -100,
            left: -100,
            width: 300,
            height: 300,
            borderRadius: "50%",
            background: "rgba(255, 255, 255, 0.1)",
            animation: "float 6s ease-in-out infinite",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            bottom: -150,
            right: -150,
            width: 400,
            height: 400,
            borderRadius: "50%",
            background: "rgba(255, 255, 255, 0.1)",
            animation: "float 8s ease-in-out infinite",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            right: "10%",
            width: 200,
            height: 200,
            borderRadius: "50%",
            background: "rgba(255, 255, 255, 0.08)",
            animation: "float 7s ease-in-out infinite",
          }}
        />

        <style>
          {`
            @keyframes float {
              0%, 100% { transform: translateY(0px) translateX(0px); }
              50% { transform: translateY(-20px) translateX(20px); }
            }
          `}
        </style>

        <Container maxWidth="xl" sx={{ pt: 2, pb: 4, position: 'relative', zIndex: 1 }}>
        {isLoading ? (
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            height: '50vh' 
          }}>
            <CircularProgress size={60} />
          </Box>
        ) : (
          <>
            {/* Friendly Welcome Section */}
            <Box sx={{ mb: 4, p: 3, bgcolor: 'rgba(255, 255, 255, 0.95)', borderRadius: 3, boxShadow: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <WavingHand sx={{ fontSize: 32, mr: 2, color: '#51647C', animation: 'wave 2s infinite' }} />
                <Typography variant="h4" sx={{ fontWeight: 600, color: '#51647C' }}>
                  Welcome to Your Rowad Ticketing System! 
                </Typography>
              </Box>
              <Typography variant="body1" sx={{ mb: 2, opacity: 0.9 }}>
You are now able to create tickets whenever you encounter any issue, follow up on the ticket status, and chat directly with the support team.
<br/>Please feel free to submit a ticket to IT, Unifier, Oracle, or ERP. Additional departments will be added soon to support your requests even more efficiently.
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Typography variant="body2" sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  bgcolor: 'rgba(255,255,255,0.2)', 
                  px: 2, 
                  py: 0.5, 
                  borderRadius: 2 
                }}>
                  <Help sx={{ fontSize: 16, mr: 1 }} />
                  Need help? Just create a new ticket!
                </Typography>
                <Typography variant="body2" sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  bgcolor: 'rgba(255,255,255,0.2)', 
                  px: 2, 
                  py: 0.5, 
                  borderRadius: 2 
                }}>
                  <EmojiEmotions sx={{ fontSize: 16, mr: 1 }} />
                  Our support team is ready to assist you
                </Typography>
              </Box>
            </Box>

            {/* Friendly Statistics Cards */}
            <Box sx={{ mb: 3, bgcolor: 'rgba(255, 255, 255, 0.95)', p: 2, borderRadius: 2, boxShadow: 2 }}>
              <Typography variant="h5" sx={{ display: 'flex', alignItems: 'center', fontWeight: 600 }}>
                📊 Your Ticket Overview
                <Typography variant="body2" sx={{ ml: 2, opacity: 0.7, fontWeight: 400 }}>
                  Here's how things are looking for you today
                </Typography>
              </Typography>
            </Box>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ textAlign: 'center', bgcolor: 'primary.light', color: 'white', transform: 'scale(1)', transition: 'transform 0.2s', '&:hover': { transform: 'scale(1.02)' } }}>
                  <CardContent>
                    <Assignment sx={{ fontSize: 40, mb: 1 }} />
                    <Typography variant="h4">{stats.total}</Typography>
                    <Typography variant="body2">Your Requests</Typography>
                    <Typography variant="caption" sx={{ opacity: 0.8 }}>
                      Total tickets submitted
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ textAlign: 'center', bgcolor: 'success.light', color: 'white', transform: 'scale(1)', transition: 'transform 0.2s', '&:hover': { transform: 'scale(1.02)' } }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 1 }}>
                      <Celebration sx={{ fontSize: 40 }} />
                    </Box>
                    <Typography variant="h4">{stats.closed}</Typography>
                    <Typography variant="body2">All Done! 🎉</Typography>
                    <Typography variant="caption" sx={{ opacity: 0.8 }}>
                      Successfully resolved
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              {/* <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ textAlign: 'center', bgcolor: 'info.light', color: 'white', transform: 'scale(1)', transition: 'transform 0.2s', '&:hover': { transform: 'scale(1.02)' } }}>
                  <CardContent>
                    <ThumbUp sx={{ fontSize: 40, mb: 1 }} />
                    <Typography variant="h4">{stats.closed}</Typography>
                    <Typography variant="body2">Completed ✅</Typography>
                    <Typography variant="caption" sx={{ opacity: 0.8 }}>
                      Issues closed & done
                    </Typography>
                  </CardContent>
                </Card>
              </Grid> */}
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ textAlign: 'center', bgcolor: 'warning.light', color: 'white', transform: 'scale(1)', transition: 'transform 0.2s', '&:hover': { transform: 'scale(1.02)' } }}>
                  <CardContent>
                    <Timeline sx={{ fontSize: 40, mb: 1 }} />
                    <Typography variant="h4">{stats.inProgress}</Typography>
                    <Typography variant="body2">We're On It! 🚀</Typography>
                    <Typography variant="caption" sx={{ opacity: 0.8 }}>
                      Being worked on now
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Enhanced Filters */}
            <Paper sx={{ p: 3, mb: 3, bgcolor: 'rgba(255, 255, 255, 0.95)', boxShadow: 3, border: '1px solid', borderColor: 'rgba(0,0,0,0.1)' }}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', fontWeight: 600 }}>
                  🔍 Find What You're Looking For
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Use these friendly filters to quickly find your tickets
                </Typography>
              </Box>
              
              <Grid container spacing={2}>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    placeholder="Search tickets..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                      startAdornment: <Search sx={{ color: 'action.active', mr: 1 }} />
                    }}
                  />
                </Grid>
                
                <Grid item xs={12} md={2}>
                  <FormControl fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={statusFilter}
                      label="Status"
                      onChange={(e) => setStatusFilter(e.target.value)}
                    >
                      <MenuItem value="All">All Statuses</MenuItem>
                      {uniqueStatuses.map(status => (
                        <MenuItem key={status} value={status}>{status}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={2}>
                  <FormControl fullWidth>
                    <InputLabel>Category</InputLabel>
                    <Select
                      value={categoryFilter}
                      label="Category"
                      onChange={(e) => setCategoryFilter(e.target.value)}
                    >
                      <MenuItem value="All">All Categories</MenuItem>
                      {uniqueCategories.map(category => (
                        <MenuItem key={category} value={category}>{category}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={2}>
                  <FormControl fullWidth>
                    <InputLabel>Date Range</InputLabel>
                    <Select
                      value={dateRange}
                      label="Date Range"
                      onChange={(e) => setDateRange(e.target.value)}
                    >
                      <MenuItem value="All">All Time</MenuItem>
                      <MenuItem value="Today">Last 24 Hours</MenuItem>
                      <MenuItem value="Week">Last Week</MenuItem>
                      <MenuItem value="Month">Last Month</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={3}>
                  <FormControl fullWidth>
                    <InputLabel>Sort By</InputLabel>
                    <Select
                      value={`${sortBy}-${sortOrder}`}
                      label="Sort By"
                      onChange={(e) => {
                        const [field, order] = e.target.value.split('-');
                        setSortBy(field);
                        setSortOrder(order);
                      }}
                    >
                      <MenuItem value="CreatedOn-desc">Newest First</MenuItem>
                      <MenuItem value="CreatedOn-asc">Oldest First</MenuItem>
                      <MenuItem value="Name-asc">Name A-Z</MenuItem>
                      <MenuItem value="Name-desc">Name Z-A</MenuItem>
                      <MenuItem value="Status-asc">Status A-Z</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Paper>

            {/* Friendly Results Summary */}
            {/* <Box sx={{ mb: 3, p: 2, bgcolor: 'rgba(255, 255, 255, 0.95)', borderRadius: 2, boxShadow: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                <Box>
                  <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', fontWeight: 600 }}>
                    🎯 Your Personal Ticket Dashboard
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {filteredCases.length === cases.length 
                      ? `You have ${cases.length} ticket${cases.length !== 1 ? 's' : ''} in total` 
                      : `Showing ${filteredCases.length} of ${cases.length} tickets based on your filters`
                    }
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => setShowNewTicketDialog(true)}
                    startIcon={<Add />}
                    size="small"
                    sx={{ borderRadius: 2 }}
                  >
                    ➕ Create New Ticket
                  </Button>
       
                </Box>
              </Box>
            </Box> */}

            {/* Enhanced Ticket Cards */}
            {filteredCases.length > 0 ? (
              <Grid container spacing={2}>
                {filteredCases.map((ticket, index) => (
                  <Grid item xs={12} lg={6} key={index}>
                    <Card sx={{ 
                      height: '100%', 
                      display: 'flex', 
                      flexDirection: 'column',
                      border: ticket.priority === 'High' ? '2px solid #f44336' : 'none',
                      boxShadow: ticket.priority === 'High' ? 6 : 3,
                      bgcolor: 'rgba(255, 255, 255, 0.95)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        boxShadow: 8,
                        transform: 'translateY(-4px)',
                      },
                    }}>
                      <CardContent sx={{ flexGrow: 1 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            {getStatusIcon(ticket.Status)}
                            <Typography variant="h6" sx={{ ml: 1, fontWeight: 'bold' }}>
                              {ticket.Name}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Chip 
                              label={ticket.priority} 
                              size="small" 
                              color={ticket.priority === 'High' ? 'error' : ticket.priority === 'Medium' ? 'warning' : 'default'}
                            />
                            <Chip 
                              label={ticket.Status} 
                              size="small" 
                              color={ticket.color}
                            />
                          </Box>
                        </Box>

                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          <strong>Category:</strong> {ticket.Category}
                        </Typography>

                        {/* Accordion and Actions in horizontal layout */}
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
                          <Accordion sx={{ boxShadow: 'none', border: '1px solid #e0e0e0', flex: 1 }}>
                            <AccordionSummary expandIcon={<ExpandMore />}>
                              <Typography variant="body2">
                                <strong>Description</strong>
                              </Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                              <Typography variant="body2" sx={{ mb: 2 }}>
                                {ticket.Description}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                <strong>Owner:</strong> {ticket.Owner}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                <strong>Comments:</strong> {ticket.Comments}
                              </Typography>
                            </AccordionDetails>
                          </Accordion>

                          {/* Action buttons on the right */}
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'center', pt: 1 }}>
                            <Tooltip title="View Details">
                              <IconButton onClick={() => handleDetailsClick(ticket)} color="primary" size="small">
                                <Visibility />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Chat">
                              <IconButton onClick={() => handleChatClick(ticket)} color="primary" size="small">
                                <Chat />
                              </IconButton>
                            </Tooltip>
                            {(ticket.Status.toLowerCase() === 'closed' || ticket.Status === 'Resolved' || ticket.Status === 'Site Visit + Resolved') && (!ticket.Feedback || ticket.Feedback === null) && (
                              <Tooltip title="Give Feedback">
                                <IconButton onClick={() => handleFeedbackClick(ticket)} color="secondary" size="small">
                                  <Feedback />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </Box>

                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="caption" color="text.secondary">
                            Created: {ticket.FormattedDate}
                          </Typography>
                          <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                            ID: {ticket.ID}
                          </Typography>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Paper sx={{ p: 6, textAlign: 'center', bgcolor: 'rgba(255, 255, 255, 0.95)', boxShadow: 3, border: '2px dashed', borderColor: 'rgba(0,0,0,0.2)' }}>
                <Box sx={{ mb: 3 }}>
                  <EmojiEmotions sx={{ fontSize: 64, color: 'info.main', mb: 2 }} />
                </Box>
                <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: 'text.primary' }}>
                  {searchTerm || statusFilter !== 'All' || categoryFilter !== 'All' || dateRange !== 'All'
                    ? "Hmm, we couldn't find any tickets matching your search 🤔"
                    : "You're all caught up! 🎉"
                  }
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 400, mx: 'auto' }}>
                  {searchTerm || statusFilter !== 'All' || categoryFilter !== 'All' || dateRange !== 'All'
                    ? "Try adjusting your filters or search terms. Don't worry, your tickets are safe and sound!"
                    : "No tickets to show right now. When you need help, just create a new ticket and we'll be right on it!"
                  }
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                  {(searchTerm || statusFilter !== 'All' || categoryFilter !== 'All' || dateRange !== 'All') && (
                    <Button
                      variant="outlined"
                      onClick={() => {
                        setSearchTerm('');
                        setStatusFilter('All');
                        setCategoryFilter('All');
                        setDateRange('All');
                      }}
                      sx={{ borderRadius: 2 }}
                    >
                      🔄 Clear All Filters
                    </Button>
                  )}
                  <Button
                    variant="contained"
                    onClick={() => setShowNewTicketDialog(true)}
                    startIcon={<Add />}
                    sx={{ borderRadius: 2 }}
                  >
                    ➕ Create Your First Ticket
                  </Button>
                </Box>
              </Paper>
            )}

            {/* Ticket Details Dialog */}
            <Dialog
              open={showDetails}
              onClose={() => setShowDetails(false)}
              maxWidth="md"
              fullWidth
            >
              <DialogTitle>
                Ticket Details: {selectedTicket?.Name}
              </DialogTitle>
              <DialogContent>
                {selectedTicket && (
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2"><strong>ID:</strong> {selectedTicket.ID}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2"><strong>Status:</strong> {selectedTicket.Status}</Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="body2"><strong>Category:</strong> {selectedTicket.Category}</Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="body2"><strong>Description:</strong></Typography>
                      <Typography variant="body2" sx={{ mt: 1, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                        {selectedTicket.Description}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2"><strong>Owner:</strong> {selectedTicket.Owner}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2"><strong>Priority:</strong> {selectedTicket.priority}</Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="body2"><strong>Comments:</strong></Typography>
                      <Typography variant="body2" sx={{ mt: 1, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                        {selectedTicket.Comments}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="body2"><strong>Created:</strong> {selectedTicket.FormattedDate}</Typography>
                    </Grid>
                  </Grid>
                )}
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setShowDetails(false)}>Close</Button>
                <Button 
                  variant="contained" 
                  onClick={() => {
                    setShowDetails(false);
                    handleChatClick(selectedTicket);
                  }}
                  startIcon={<Chat />}
                >
                  Start Chat
                </Button>
              </DialogActions>
            </Dialog>

            {/* Enhanced New Ticket Dialog */}
            <EnhancedNewTicketDialog
              open={showNewTicketDialog}
              onClose={() => setShowNewTicketDialog(false)}
              onTicketCreated={RetrieveData}
            />

            {/* UI Enhancement Ticket Dialog */}
            <UIEnhancementTicket
              open={showUIEnhancementDialog}
              onClose={() => setShowUIEnhancementDialog(false)}
              onSubmit={handleUIEnhancementSubmit}
            />

            {/* Feedback Dialog */}
            <Dialog 
              open={feedbackDialog.open} 
              onClose={handleCloseFeedbackDialog}
              maxWidth="sm"
              fullWidth
            >
              <DialogTitle>
                Rate Your Experience
              </DialogTitle>
              <DialogContent>
                <Box sx={{ mb: 3, textAlign: 'center' }}>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    How would you rate the support you received for ticket: <strong>{feedbackDialog.ticket?.Name}</strong>?
                  </Typography>
                  <Rating
                    name="feedback-rating"
                    value={feedbackRating}
                    onChange={(event, newValue) => setFeedbackRating(newValue)}
                    size="large"
                    sx={{ fontSize: '2.5rem' }}
                  />
                </Box>
                
                <TextField
                  label="Comments (Optional)"
                  multiline
                  rows={4}
                  fullWidth
                  value={feedbackComment}
                  onChange={handleCommentChange}
                  placeholder="Share your experience or suggestions for improvement..."
                  helperText={`${feedbackComment.length}/1900 characters`}
                  variant="outlined"
                />
              </DialogContent>
              <DialogActions>
                <Button 
                  onClick={handleCloseFeedbackDialog}
                  disabled={submittingFeedback}
                >
                  Cancel
                </Button>
                <Button 
                  onClick={handleSubmitFeedback}
                  variant="contained"
                  disabled={submittingFeedback || feedbackRating === 0}
                >
                  {submittingFeedback ? 'Submitting...' : 'Submit Feedback'}
                </Button>
              </DialogActions>
            </Dialog>
          </>
        )}
        </Container>
      </Box>

      {/* Chat Dialog */}
      <Dialog
        open={showChat}
        onClose={handleBackToTickets}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            height: '80vh',
            maxHeight: '800px'
          }
        }}
      >
        <DialogTitle sx={{ 
          background: '#51647C',
          color: 'white',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <Typography variant="h6">
            Chat - {selectedTicket?.Name}
          </Typography>
          <IconButton 
            onClick={handleBackToTickets}
            sx={{ color: 'white' }}
          >
            <ArrowBack />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, height: '100%' }}>
          {selectedTicket && (
            <TicketChat
              ticketId={selectedTicket.ID}
              ticketName={selectedTicket.Name}
              onBack={handleBackToTickets}
              inDialog={true}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default EnhancedTicketTable;
